// Simple S3 table loader that bypasses Papa Parse and uses direct fetch
// This uses the same approach as S3DebugComponent which we know works

import { getUrl } from 'aws-amplify/storage';

interface ITable {
  id: string;
  caption: string;
  source: string;
  view: string;
  group: string;
  selected: boolean;
}

export async function loadTablesFromS3(
  source: string,
): Promise<ITable[] | null> {
  console.log(`🎯 loadTablesFromS3: Starting with source: ${source}`);

  try {
    // Get S3 pre-signed URL using the EXACT same path that works in S3DebugComponent
    const exactPath = `protected/describeTable/${source}`;
    console.log(`🎯 loadTablesFromS3: Using exact working path: ${exactPath}`);

    const linkResult = await getUrl({ path: exactPath });
    const link = linkResult.url.toString();

    if (
      !link ||
      link.includes('placeholder-url') ||
      link === 'placeholder-url'
    ) {
      console.error(
        `❌ loadTablesFromS3: getUrl returned placeholder for ${exactPath}: ${link}`,
      );
      return null;
    }

    console.log(`✅ loadTablesFromS3: Got real S3 URL: ${link}`);
    console.log(`🔗 S3 URL: ${link}`);

    // Check for placeholder URL before making request
    if (link.includes('placeholder-url') || link === 'placeholder-url') {
      console.error(
        `❌ loadTablesFromS3: Received placeholder URL instead of real S3 URL!`,
      );
      console.error(
        `🔧 This indicates AWS Amplify Storage configuration issue.`,
      );
      console.error(
        `📋 The issue is NOT with the CSV file - it's with AWS setup.`,
      );
      return null;
    }

    // Direct fetch (same approach as S3DebugComponent)
    const response = await fetch(link);

    if (!response.ok) {
      console.error(`❌ loadTablesFromS3: HTTP ${response.status} response`);
      return null;
    }

    const csvContent = await response.text();
    const contentType = response.headers.get('content-type');

    console.log(`📊 loadTablesFromS3: Response status: ${response.status}`);
    console.log(`📊 loadTablesFromS3: Content-Type: ${contentType}`);
    console.log(
      `📊 loadTablesFromS3: Content length: ${csvContent.length} characters`,
    );

    // Check for HTML content (error response) - but be more specific
    if (
      csvContent.trim().startsWith('<!DOCTYPE html>') ||
      csvContent.trim().startsWith('<html')
    ) {
      console.error(`❌ loadTablesFromS3: Content is definitely HTML, not CSV`);
      return null;
    }

    // If content looks like CSV (comma-separated values), proceed regardless of content-type
    const firstLine = csvContent.split('\n')[0];
    if (firstLine && firstLine.includes(',')) {
      console.log(
        `✅ loadTablesFromS3: Content appears to be CSV (ignoring wrong content-type)`,
      );
    } else {
      console.error(
        `❌ loadTablesFromS3: Content doesn't look like CSV. First line: ${firstLine}`,
      );
      return null;
    }

    // Parse CSV manually
    const lines = csvContent.trim().split('\n');
    if (lines.length < 2) {
      console.error(`❌ loadTablesFromS3: CSV has insufficient data`);
      return null;
    }

    const headers = lines[0].split(',').map((h) => h.trim());
    console.log(`📋 loadTablesFromS3: CSV headers: ${headers.join(', ')}`);

    // Validate headers
    const requiredHeaders = ['id', 'caption', 'source', 'view', 'group'];
    const hasRequiredHeaders = requiredHeaders.every((header) =>
      headers.includes(header),
    );

    if (!hasRequiredHeaders) {
      console.error(
        `❌ loadTablesFromS3: Missing required headers. Expected: ${requiredHeaders.join(', ')}, Got: ${headers.join(', ')}`,
      );
      return null;
    }

    // Get header indices
    const idIndex = headers.indexOf('id');
    const captionIndex = headers.indexOf('caption');
    const sourceIndex = headers.indexOf('source');
    const viewIndex = headers.indexOf('view');
    const groupIndex = headers.indexOf('group');

    // Parse data rows
    const tableData: ITable[] = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue; // Skip empty lines

      const values = line.split(',').map((v) => v.trim());

      if (values.length >= headers.length) {
        const tableItem: ITable = {
          id: values[idIndex] || `${i}`,
          caption: values[captionIndex] || `Table ${i}`,
          source: values[sourceIndex] || 'unknown.csv',
          view: values[viewIndex] || 'unknown_view',
          group: values[groupIndex] || 'default',
          selected: i === 1, // First table selected
        };
        tableData.push(tableItem);
      }
    }

    console.log(
      `✅ loadTablesFromS3: Successfully parsed ${tableData.length} tables from S3 CSV`,
    );
    console.log(
      `📋 loadTablesFromS3: Tables:`,
      tableData.map((t) => `${t.caption} (${t.group})`).join(', '),
    );

    return tableData;
  } catch (error) {
    console.error(`❌ loadTablesFromS3: Error loading tables:`, error);
    return null;
  }
}
